import { FC, useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, ClickAwayListener, FormControl, Popper, Stack, Tooltip } from '@mui/material';
import { SxProps } from '@mui/system';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import dayjs, { Dayjs } from 'dayjs';

import { ReactComponent as CalendarOutlineIcon } from '@/assets/icons/calendar-outline-icon.svg';
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/chevron-down-icon.svg';
import { ReactComponent as FormInfo } from '@/assets/icons/form-info-icon.svg';
import { camelToSnakeCase } from '@/shared/lib/strings';
import CalendarDatePicker from '@/shared/ui/CalendarDatePicker';
import {
  COMPACT_FORM_LABEL_SX,
  COMPACT_INPUT_BASE_SX,
  FORM_LABEL_SX,
} from '@/shared/ui/form-fields/fieldStyles';
import { FormError, FormLabel } from '@/shared/ui/styled';

type Props = {
  id?: string;
  name: string;
  label?: string;
  disabled?: boolean;
  disablePast?: boolean;
  availability?: string[] | undefined;
  tooltip?: string;
  format?: string;
  sx?: SxProps;
  /** Drops the leading calendar icon and swaps the trailing chevron for the calendar-outline icon — opt-in so other RhfDatePicker consumers keep their existing look. */
  roundedVariant?: boolean;
  compact?: boolean;
  clearable?: boolean;
  onChange?: (startDate: string, endDate: string) => void;
  'data-testid'?: string;
};

const CLOSE_AFTER_PICK_MS = 200;

const COMPACT_DATE_TEXT_SX = {
  fontFamily: COMPACT_INPUT_BASE_SX.fontFamily,
  fontSize: COMPACT_INPUT_BASE_SX.fontSize,
  fontWeight: COMPACT_INPUT_BASE_SX.fontWeight,
  lineHeight: COMPACT_INPUT_BASE_SX.lineHeight,
  color: 'grey.800',
};

const RhfDatePicker: FC<Props> = ({
  id,
  name,
  label,
  disabled,
  disablePast,
  availability,
  tooltip,
  format,
  sx,
  roundedVariant,
  compact,
  clearable,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const value = watch(name);
  const pickerValue = value ? dayjs(value) : null;

  const isDateUnavailable = useCallback(
    (date: Dayjs) => {
      if (availability === undefined) {
        return false;
      }

      if (!availability?.length) {
        return true;
      }

      return !availability?.map((d) => d.split('T')[0])?.includes(date.format('YYYY-MM-DD'));
    },
    [availability],
  );

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || isSubmitting) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <Controller
      name={name}
      render={({ fieldState: { error } }) => (
        <FormControl
          fullWidth
          error={!!error}
          sx={
            roundedVariant
              ? undefined
              : { '& .MuiInputBase-input': { paddingLeft: '24px !important' } }
          }
        >
          {!!label && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel sx={compact ? COMPACT_FORM_LABEL_SX : FORM_LABEL_SX}>{label}</FormLabel>
              {!!tooltip && (
                <Tooltip title={tooltip}>
                  <span>
                    <Button type="button" sx={{ minWidth: 0, padding: '0.25rem' }} disableRipple>
                      <FormInfo />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ position: 'relative' }} onClick={handleOpen}>
              {!roundedVariant && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {compact ? (
                    <CalendarOutlineIcon />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M12.6667 6.41211C12.6143 6.4307 12.5587 6.44271 12.5 6.44271H3.16667C3.10796 6.44271 3.05233 6.4307 3 6.41211V12.1999C3 12.9237 3.57814 13.5 4.27799 13.5H11.3887C12.0885 13.5 12.6667 12.9237 12.6667 12.1999V6.41211ZM12.6667 4.82878C12.6667 4.10493 12.0885 3.52865 11.3887 3.52865H4.27799C3.57814 3.52865 3 4.10493 3 4.82878V5.47331C3.05231 5.45474 3.10799 5.44271 3.16667 5.44271H12.5C12.5587 5.44271 12.6144 5.45474 12.6667 5.47331V4.82878ZM13.6667 12.1999C13.6667 13.4643 12.6525 14.5 11.3887 14.5H4.27799C3.01417 14.5 2 13.4643 2 12.1999V4.82878C2 3.70272 2.80432 2.7583 3.87305 2.5651V2C3.87305 1.72386 4.0969 1.5 4.37305 1.5C4.64918 1.50002 4.87305 1.72387 4.87305 2V2.52865H10.6667V2C10.6667 1.72386 10.8905 1.5 11.1667 1.5C11.4428 1.5 11.6667 1.72386 11.6667 2V2.54557C12.798 2.68415 13.6667 3.65852 13.6667 4.82878V12.1999Z"
                        fill="#615E6F"
                      />
                    </svg>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {roundedVariant ? (
                  <CalendarOutlineIcon />
                ) : compact ? (
                  <ChevronDownIcon width={16} height={16} />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13 5.5L8 10.5L3 5.5"
                      stroke="#9EA5AD"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </Box>
              <DateField
                fullWidth
                value={pickerValue}
                disabled={isSubmitting || disabled}
                format={format || 'DD.MM.YYYY'}
                disablePast={disablePast}
                minDate={disablePast ? dayjs() : undefined}
                shouldDisableDate={isDateUnavailable}
                onChange={(value, context) => {
                  if (context.validationError) return;
                  setValue(name, value && value.isValid() ? dayjs(value).format('YYYY-MM-DD') : '');
                }}
                label={undefined}
                id={id ?? `${camelToSnakeCase(label ?? 'no-label')}-input`}
                error={!!error}
                sx={{
                  ...sx,
                  '& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
                    cursor: 'pointer',
                    height: roundedVariant ? '41px !important' : '40px !important',
                    padding: '12px 16px !important',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: roundedVariant ? '0.75rem !important' : '12px !important',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    color: '#615E6F',
                  },

                  '& .MuiPickersOutlinedInput-sectionsContainer': {
                    padding: '0',
                    overflow: 'visible',
                    width: 'auto',
                  },

                  ...(compact && {
                    '& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
                      ...COMPACT_DATE_TEXT_SX,
                      cursor: 'pointer',
                      height: '40px !important',
                      padding: '12px 16px !important',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '12px !important',
                      border: '1px solid rgba(0, 0, 0, 0.15)',
                    },
                    '& .MuiPickersSectionList-root, & .MuiPickersSectionList-section, & .MuiPickersInputBase-sectionContent, & .MuiInputBase-input':
                      COMPACT_DATE_TEXT_SX,
                  }),

                  '& fieldset': {
                    border: 'none !important',
                    borderRadius: '12px !important',
                  },

                  marginBottom: 0,
                }}
                data-testid={dataTestId ?? `${camelToSnakeCase(label ?? 'no-label')}-date-picker`}
              />
            </Box>

            <ClickAwayListener
              onClickAway={(event) => {
                if (anchorEl && event.target instanceof Node && anchorEl.contains(event.target))
                  return;
                handleClose();
              }}
            >
              <Popper
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: (theme) => theme.zIndex.modal }}
              >
                <CalendarDatePicker
                  value={pickerValue}
                  onClear={
                    clearable
                      ? () => {
                          setValue(name, '', { shouldDirty: true });
                          handleClose();
                        }
                      : undefined
                  }
                  minDate={disablePast ? dayjs() : undefined}
                  disablePast={disablePast}
                  isDateUnavailable={isDateUnavailable}
                  onChange={(date) => {
                    // Reflect the pick (header text + selected pill) before
                    // closing — committing and closing in the same tick left
                    // the picker looking like the click did nothing.
                    setValue(name, date.format('YYYY-MM-DD'));
                    setTimeout(handleClose, CLOSE_AFTER_PICK_MS);
                  }}
                />
              </Popper>
            </ClickAwayListener>
          </LocalizationProvider>

          {!!error && (
            <FormError>
              {error?.type === 'invalid_type' ? t('errors.required_field') : error.message}
            </FormError>
          )}
        </FormControl>
      )}
    />
  );
};

export default RhfDatePicker;
